import { FormContext, FormItem, ValidationResult } from "../form-builder.model";

/**
 * Props handed to a form widget's render(). Identical to what the built-in
 * inputs receive (see form-input.tsx), so a widget can read sibling field
 * values off `formData`, surface validation via `formErrors`, and emit its
 * value — of ANY shape — through `setValue`. `context` carries the form's
 * ambient lineage (parent-view selections) when the form was opened with one
 * — a context-dependent widget (e.g. a table-scoped SQL completer) reads its
 * scope from here rather than a side channel.
 */
export type FormWidgetProps = {
  formItem: Extract<FormItem, { type: "widget" }>;
  formData: Record<string, unknown>;
  formErrors: Record<string, ValidationResult[] | undefined>;
  setValue: (newValue: unknown) => void;
  context?: FormContext;
};

/**
 * A pluggable form widget. `name` is the key referenced by a form item's
 * `widget` field; `render` returns the element for that field.
 */
export type FormWidget = {
  name: string;
  render: (props: FormWidgetProps) => JSX.Element;
};

/**
 * Registry of pluggable form widgets — the same singleton-over-a-Map shape as
 * LayoutService (forms/layout/layout-service.ts). Widgets register themselves
 * (typically as an import side-effect of the file that uses them) and the form
 * core looks them up by name when it hits a `{ type: "widget" }` item.
 */
export class FormWidgetService {
  static readonly _instance: FormWidgetService = new FormWidgetService();

  static get() {
    return this._instance;
  }

  private widgets = new Map<string, FormWidget>();

  register(widget: FormWidget) {
    this.widgets.set(widget.name, widget);
  }

  list() {
    return this.widgets.keys();
  }

  get(name: string): FormWidget | undefined {
    return this.widgets.get(name);
  }
}
